import { PDFDocument, rgb, PDFPage } from 'pdf-lib';
import { PhotoMarker } from '../types/types';

/**
 * Downloads a PDF layout with markers overlayed as vector graphics
 * 
 * @param pdfUrl URL to the original PDF file
 * @param markers Array of photo markers to overlay
 * @param scale Current zoom scale (to calculate actual marker positions)
 * @param fileName Name for the downloaded file (without extension)
 */
export const downloadLayoutPDF = async (
  pdfUrl: string,
  markers: PhotoMarker[],
  scale: number,
  fileName: string = 'layout-with-markers'
): Promise<void> => {
  try {
    console.log('[downloadLayoutPDF] Starting PDF download with markers:', {
      pdfUrl,
      markerCount: markers.length,
      scale,
    });

    // Fetch the original PDF
    const response = await fetch(pdfUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    }
    
    const pdfBytes = await response.arrayBuffer();
    
    // Load the PDF document
    const pdfDoc = await PDFDocument.load(pdfBytes);
    
    // Get the first page (assuming single-page layout PDFs)
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const { width: pageWidth, height: pageHeight } = firstPage.getSize();
    
    console.log('[downloadLayoutPDF] PDF page dimensions:', { pageWidth, pageHeight });
    
    // Draw markers on the PDF
    markers.forEach((marker) => {
      drawMarkerOnPage(firstPage, marker);
    });
    
    // Serialize the modified PDF
    const modifiedPdfBytes = await pdfDoc.save();
    
    // Create blob and trigger download
    const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
    
    console.log('[downloadLayoutPDF] PDF downloaded successfully');
  } catch (error) {
    console.error('Error downloading PDF with markers:', error);
    throw error;
  }
};

/**
 * Draw a photo marker on a PDF page
 * Matches the red rectangle style from the viewer
 */
function drawMarkerOnPage(
  page: PDFPage,
  marker: PhotoMarker
): void {
  const { height: pageHeightPdf } = page.getSize();
  
  // Convert marker position from SVG coordinates to PDF coordinates
  // SVG origin is top-left, PDF origin is bottom-left
  const x = marker.x;
  const y = pageHeightPdf - marker.y; // Flip Y coordinate
  
  // Marker dimensions (matching the viewer)
  const markerWidth = marker.width || 50;
  const markerHeight = marker.height || 50;
  
  // Draw rectangle background (red fill, black stroke)
  // PDF coordinate system: origin at bottom-left
  const rectX = x - markerWidth / 2;
  const rectY = y - markerHeight / 2;
  
  page.drawRectangle({
    x: rectX,
    y: rectY,
    width: markerWidth,
    height: markerHeight,
    color: rgb(1, 0, 0), // Red fill
    borderColor: rgb(0, 0, 0), // Black stroke
    borderWidth: 1,
    opacity: 1,
  });
  
  // Draw marker number (white text, centered)
  const fontSize = marker.fontSize || 24;
  const numberText = marker.number.toString();
  
  // Estimate text width (rough approximation)
  const textWidth = numberText.length * (fontSize * 0.6);
  
  page.drawText(numberText, {
    x: x - textWidth / 2,
    y: y - fontSize / 3,
    size: fontSize,
    color: rgb(1, 1, 1), // White text
  });
}
